import { Tag, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useInvoicesDataTable } from 'generated/resources/datatable.graphql'
import { compact } from 'lodash'

import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import Actions from 'modules/finance/components/Invoices/Actions'
import { DocumentType } from 'modules/finance/components/Invoices/DocumentType'
import { Status } from 'modules/finance/components/Invoices/Status'
import { Validated } from 'modules/finance/components/Invoices/Validated'
import { contactFullName } from 'modules/finance/lib/contactsUtils'

// This type will be automatically generated by the plugin, so we will be able to import it from the generated file
type InvoicesDataTableProjection = Parameters<typeof useInvoicesDataTable>[0][number]

const InvoiceVendorProjection = (): InvoicesDataTableProjection => ({
  identifier: 'vendor',
  title: i18n.t('finance.invoices.list.fields.vendor'),
  kind: ProjectionKind.Custom,
  render: ({ vendor: tableVendor }) => (
    <Text.Normal size="200">{contactFullName(tableVendor)}</Text.Normal>
  ),
  template: '{{ vendor.legalName }}',
})

const InvoiceNumberProjection = (): InvoicesDataTableProjection => ({
  identifier: 'documentNumber',
  title: i18n.t('finance.invoices.list.fields.invoice_number'),
  kind: ProjectionKind.Custom,
  render: ({ documentNumber, duplicateFinancialDocumentId }) => (
    <>
      {duplicateFinancialDocumentId ? (
        <Tag leftItem="icon" icon="alert-circle" inline showCircle size="normal" type="tangerine">
          {i18n.t('finance.invoices.callouts.duplicate_invoice.alert')}
        </Tag>
      ) : (
        <Text.Normal size="200">{documentNumber || '-'}</Text.Normal>
      )}
    </>
  ),
  template: '{{ documentNumber }}',
})

const InvoiceDocumentTypeProjection = (): InvoicesDataTableProjection => ({
  identifier: 'documentType',
  title: i18n.t('finance.invoices.list.fields.document_type'),
  kind: ProjectionKind.Custom,
  sortBy: 'documentType',
  render: ({ documentType }) => <DocumentType documentType={documentType} />,
  template: '{{ documentType }}',
})

const InvoiceDocumentDateProjection = (): InvoicesDataTableProjection => ({
  identifier: 'documentDate',
  title: i18n.t('finance.invoices.list.fields.date'),
  kind: ProjectionKind.Custom,
  sortBy: 'documentDate',
  render: ({ documentDate }) => (
    <Text.Normal size="200">
      {documentDate ? dateBackendDateTimeToUI(String(documentDate)) : '-'}
    </Text.Normal>
  ),
  template: '{{ documentDate }}',
})

const InvoiceAmountProjection = (locale: string): InvoicesDataTableProjection => ({
  identifier: 'amount',
  title: i18n.t('finance.invoices.list.fields.amount'),
  sortBy: 'totalAmountCents',
  kind: ProjectionKind.Custom,
  render: ({ totalAmountCents, currency }) => (
    <Text.Normal size="200">
      {totalAmountCents && currency
        ? formattedAmount({
            amountCents: Number(totalAmountCents),
            currency,
            locale,
          })
        : '-'}
    </Text.Normal>
  ),
  template: '{{ totalAmountCents }}',
})

const InvoicePaymentStatusProjection = (): InvoicesDataTableProjection => ({
  identifier: 'status',
  title: i18n.t('finance.invoices.list.fields.payment_status'),
  sortBy: 'status',
  kind: ProjectionKind.Custom,
  render: ({ status }) => <Status status={status} />,
  template: '{{ status }}',
})

const InvoiceValidationStatusProjection = (): InvoicesDataTableProjection => ({
  identifier: 'validated',
  title: i18n.t('finance.invoices.list.fields.validation_status'),
  kind: ProjectionKind.Custom,
  render: ({ validatedAt }) => <Validated validated={!!validatedAt} />,
  template: '{{ validated_at }}',
})

const InvoiceActionsProjection = (): InvoicesDataTableProjection => ({
  identifier: 'actions',
  kind: ProjectionKind.Decorative,
  width: 50,
  render: ({ id }) => {
    return <Actions invoiceId={Number(id)} />
  },
})

const InvoiceReceiverProjection = (): InvoicesDataTableProjection => ({
  identifier: 'legalEntity',
  title: i18n.t('finance.invoices.list.fields.receiver'),
  kind: ProjectionKind.Custom,
  render: ({ legalEntity: legalEntity }) => (
    <Text.Normal size="200">{legalEntity?.legalName || '-'}</Text.Normal>
  ),
  template: '{{ legalEntity.legalName }}',
  hidden: true,
})

export const invoicesDataTableProjections = ({
  locale,
  skipVendor,
}: {
  locale: string
  skipVendor: boolean
}) =>
  compact([
    !skipVendor && InvoiceVendorProjection(),
    InvoiceReceiverProjection(),
    InvoiceNumberProjection(),
    InvoiceDocumentTypeProjection(),
    InvoiceDocumentDateProjection(),
    InvoiceAmountProjection(locale),
    InvoicePaymentStatusProjection(),
    InvoiceValidationStatusProjection(),
    InvoiceActionsProjection(),
  ])
